import { type CattleAgeClass } from './age-classes-cattle'
import { type SheepAgeClass } from './age-classes-sheep'

// Quarterly average valuation rates in $/kg liveweight (lwt).
//
// Derived from MLA NLRS API indicator averages over each calendar quarter.
// Run packages/scripts/src/update-livestock-valuation-rates.ts to append the latest quarter.
//
// Cattle: MLA indicators are in c/kg lwt → divide by 100.
// Sheep:  MLA indicators are in c/kg cwt → divide by 100, then multiply by dressing % (0.47).
//
// Where no direct indicator exists, a multiplier is applied to the closest indicator:
//   Cattle:
//     CALF_UNWEANED  — Young Cattle #14 × 0.85
//     COW_FIRST_CALF — Processor Cow #13 × 1.10
//     BULL_BREEDING  — Heavy Steer #4 × 1.10
//   Sheep:
//     LAMB_UNWEANED  — Restocker Lamb #10 × 0.90
//     MAIDEN_EWE     — Mutton #11 × 1.10
//     RAM_BREEDING   — Heavy Lamb #8 × 1.10

export type QuarterKey = `${number}-Q${1 | 2 | 3 | 4}`

export type QuarterlyValuationRates = {
  cattle: Record<CattleAgeClass, number>
  sheep: Record<SheepAgeClass, number>
}

export const QUARTERLY_VALUATION_RATES: Record<QuarterKey, QuarterlyValuationRates> = {
  '2024-Q1': {
    cattle: {
      CALF_UNWEANED: 2.57, // Young Cattle #14 × 0.85
      WEANERS: 3.39, // Restocker Yearling Steer #2
      YEARLINGS: 3.02, // Young Cattle #14
      HEIFER_24_30M: 2.5, // Restocker Yearling Heifer #12
      HEIFER_30_36M: 2.54, // Feeder Heifer #17
      STEER_18_24M: 2.9, // Feeder Steer #3
      STEER_24_36M: 2.74, // Heavy Steer #4
      COW_FIRST_CALF: 2.43, // Processor Cow #13 × 1.10
      COW_MATURE: 2.21, // Processor Cow #13
      BULL_BREEDING: 3.01, // Heavy Steer #4 × 1.10
    },
    sheep: {
      LAMB_UNWEANED: 2.47, // Restocker Lamb #10 × 0.90, cwt→lwt
      LAMB_WEANER: 2.82, // Light Lamb #6, cwt→lwt
      HOGGET: 3.15, // Trade Lamb #7, cwt→lwt
      MAIDEN_EWE: 1.52, // Mutton #11 × 1.10, cwt→lwt
      EWE_FIRST_LAMB: 1.38, // Mutton #11, cwt→lwt
      EWE_MATURE: 1.38, // Mutton #11, cwt→lwt
      WETHER_12_24M: 3.15, // Trade Lamb #7, cwt→lwt
      WETHER_24M_PLUS: 1.38, // Mutton #11, cwt→lwt
      RAM_BREEDING: 3.62, // Heavy Lamb #8 × 1.10, cwt→lwt
    },
  },
  '2024-Q2': {
    cattle: {
      CALF_UNWEANED: 2.69, // Young Cattle #14 × 0.85
      WEANERS: 3.53, // Restocker Yearling Steer #2
      YEARLINGS: 3.16, // Young Cattle #14
      HEIFER_24_30M: 2.57, // Restocker Yearling Heifer #12
      HEIFER_30_36M: 2.63, // Feeder Heifer #17
      STEER_18_24M: 3.04, // Feeder Steer #3
      STEER_24_36M: 2.9, // Heavy Steer #4
      COW_FIRST_CALF: 2.5, // Processor Cow #13 × 1.10
      COW_MATURE: 2.27, // Processor Cow #13
      BULL_BREEDING: 3.19, // Heavy Steer #4 × 1.10
    },
    sheep: {
      LAMB_UNWEANED: 2.56, // Restocker Lamb #10 × 0.90, cwt→lwt
      LAMB_WEANER: 2.89, // Light Lamb #6, cwt→lwt
      HOGGET: 3.25, // Trade Lamb #7, cwt→lwt
      MAIDEN_EWE: 1.64, // Mutton #11 × 1.10, cwt→lwt
      EWE_FIRST_LAMB: 1.49, // Mutton #11, cwt→lwt
      EWE_MATURE: 1.49, // Mutton #11, cwt→lwt
      WETHER_12_24M: 3.25, // Trade Lamb #7, cwt→lwt
      WETHER_24M_PLUS: 1.49, // Mutton #11, cwt→lwt
      RAM_BREEDING: 3.76, // Heavy Lamb #8 × 1.10, cwt→lwt
    },
  },
  '2024-Q3': {
    cattle: {
      CALF_UNWEANED: 2.98, // Young Cattle #14 × 0.85
      WEANERS: 3.88, // Restocker Yearling Steer #2
      YEARLINGS: 3.5, // Young Cattle #14
      HEIFER_24_30M: 2.82, // Restocker Yearling Heifer #12
      HEIFER_30_36M: 2.88, // Feeder Heifer #17
      STEER_18_24M: 3.34, // Feeder Steer #3
      STEER_24_36M: 3.18, // Heavy Steer #4
      COW_FIRST_CALF: 2.75, // Processor Cow #13 × 1.10
      COW_MATURE: 2.5, // Processor Cow #13
      BULL_BREEDING: 3.5, // Heavy Steer #4 × 1.10
    },
    sheep: {
      LAMB_UNWEANED: 2.85, // Restocker Lamb #10 × 0.90, cwt→lwt
      LAMB_WEANER: 3.19, // Light Lamb #6, cwt→lwt
      HOGGET: 3.63, // Trade Lamb #7, cwt→lwt
      MAIDEN_EWE: 1.93, // Mutton #11 × 1.10, cwt→lwt
      EWE_FIRST_LAMB: 1.75, // Mutton #11, cwt→lwt
      EWE_MATURE: 1.75, // Mutton #11, cwt→lwt
      WETHER_12_24M: 3.63, // Trade Lamb #7, cwt→lwt
      WETHER_24M_PLUS: 1.75, // Mutton #11, cwt→lwt
      RAM_BREEDING: 4.21, // Heavy Lamb #8 × 1.10, cwt→lwt
    },
  },
  '2024-Q4': {
    cattle: {
      CALF_UNWEANED: 3.03, // Young Cattle #14 × 0.85
      WEANERS: 3.97, // Restocker Yearling Steer #2
      YEARLINGS: 3.56, // Young Cattle #14
      HEIFER_24_30M: 2.91, // Restocker Yearling Heifer #12
      HEIFER_30_36M: 2.95, // Feeder Heifer #17
      STEER_18_24M: 3.38, // Feeder Steer #3
      STEER_24_36M: 3.26, // Heavy Steer #4
      COW_FIRST_CALF: 2.83, // Processor Cow #13 × 1.10
      COW_MATURE: 2.57, // Processor Cow #13
      BULL_BREEDING: 3.59, // Heavy Steer #4 × 1.10
    },
    sheep: {
      LAMB_UNWEANED: 2.83, // Restocker Lamb #10 × 0.90, cwt→lwt
      LAMB_WEANER: 3.21, // Light Lamb #6, cwt→lwt
      HOGGET: 3.59, // Trade Lamb #7, cwt→lwt
      MAIDEN_EWE: 1.85, // Mutton #11 × 1.10, cwt→lwt
      EWE_FIRST_LAMB: 1.68, // Mutton #11, cwt→lwt
      EWE_MATURE: 1.68, // Mutton #11, cwt→lwt
      WETHER_12_24M: 3.59, // Trade Lamb #7, cwt→lwt
      WETHER_24M_PLUS: 1.68, // Mutton #11, cwt→lwt
      RAM_BREEDING: 4.14, // Heavy Lamb #8 × 1.10, cwt→lwt
    },
  },
  '2025-Q1': {
    cattle: {
      CALF_UNWEANED: 3.19, // Young Cattle #14 × 0.85
      WEANERS: 4.07, // Restocker Yearling Steer #2
      YEARLINGS: 3.75, // Young Cattle #14
      HEIFER_24_30M: 3.1, // Restocker Yearling Heifer #12
      HEIFER_30_36M: 3.16, // Feeder Heifer #17
      STEER_18_24M: 3.7, // Feeder Steer #3
      STEER_24_36M: 3.6, // Heavy Steer #4
      COW_FIRST_CALF: 3.19, // Processor Cow #13 × 1.10
      COW_MATURE: 2.9, // Processor Cow #13
      BULL_BREEDING: 3.96, // Heavy Steer #4 × 1.10
    },
    sheep: {
      LAMB_UNWEANED: 3.03, // Restocker Lamb #10 × 0.90, cwt→lwt
      LAMB_WEANER: 3.35, // Light Lamb #6, cwt→lwt
      HOGGET: 3.7, // Trade Lamb #7, cwt→lwt
      MAIDEN_EWE: 2.01, // Mutton #11 × 1.10, cwt→lwt
      EWE_FIRST_LAMB: 1.83, // Mutton #11, cwt→lwt
      EWE_MATURE: 1.83, // Mutton #11, cwt→lwt
      WETHER_12_24M: 3.7, // Trade Lamb #7, cwt→lwt
      WETHER_24M_PLUS: 1.83, // Mutton #11, cwt→lwt
      RAM_BREEDING: 4.16, // Heavy Lamb #8 × 1.10, cwt→lwt
    },
  },
  '2025-Q2': {
    cattle: {
      CALF_UNWEANED: 3.19, // Young Cattle #14 × 0.85
      WEANERS: 3.93, // Restocker Yearling Steer #2
      YEARLINGS: 3.76, // Young Cattle #14
      HEIFER_24_30M: 3.19, // Restocker Yearling Heifer #12
      HEIFER_30_36M: 3.37, // Feeder Heifer #17
      STEER_18_24M: 3.84, // Feeder Steer #3
      STEER_24_36M: 3.55, // Heavy Steer #4
      COW_FIRST_CALF: 3.09, // Processor Cow #13 × 1.10
      COW_MATURE: 2.81, // Processor Cow #13
      BULL_BREEDING: 3.9, // Heavy Steer #4 × 1.10
    },
    sheep: {
      LAMB_UNWEANED: 3.37, // Restocker Lamb #10 × 0.90, cwt→lwt
      LAMB_WEANER: 3.66, // Light Lamb #6, cwt→lwt
      HOGGET: 4.21, // Trade Lamb #7, cwt→lwt
      MAIDEN_EWE: 2.88, // Mutton #11 × 1.10, cwt→lwt
      EWE_FIRST_LAMB: 2.62, // Mutton #11, cwt→lwt
      EWE_MATURE: 2.62, // Mutton #11, cwt→lwt
      WETHER_12_24M: 4.21, // Trade Lamb #7, cwt→lwt
      WETHER_24M_PLUS: 2.62, // Mutton #11, cwt→lwt
      RAM_BREEDING: 4.66, // Heavy Lamb #8 × 1.10, cwt→lwt
    },
  },
  '2025-Q3': {
    cattle: {
      CALF_UNWEANED: 3.86, // Young Cattle #14 × 0.85
      WEANERS: 4.52, // Restocker Yearling Steer #2
      YEARLINGS: 4.54, // Young Cattle #14
      HEIFER_24_30M: 3.95, // Restocker Yearling Heifer #12
      HEIFER_30_36M: 4.09, // Feeder Heifer #17
      STEER_18_24M: 4.54, // Feeder Steer #3
      STEER_24_36M: 4.05, // Heavy Steer #4
      COW_FIRST_CALF: 3.97, // Processor Cow #13 × 1.10
      COW_MATURE: 3.6, // Processor Cow #13
      BULL_BREEDING: 4.46, // Heavy Steer #4 × 1.10
    },
    sheep: {
      LAMB_UNWEANED: 4.28, // Restocker Lamb #10 × 0.90, cwt→lwt
      LAMB_WEANER: 4.73, // Light Lamb #6, cwt→lwt
      HOGGET: 5.51, // Trade Lamb #7, cwt→lwt
      MAIDEN_EWE: 3.77, // Mutton #11 × 1.10, cwt→lwt
      EWE_FIRST_LAMB: 3.43, // Mutton #11, cwt→lwt
      EWE_MATURE: 3.43, // Mutton #11, cwt→lwt
      WETHER_12_24M: 5.51, // Trade Lamb #7, cwt→lwt
      WETHER_24M_PLUS: 3.43, // Mutton #11, cwt→lwt
      RAM_BREEDING: 6.01, // Heavy Lamb #8 × 1.10, cwt→lwt
    },
  },
  '2025-Q4': {
    cattle: {
      CALF_UNWEANED: 3.91, // Young Cattle #14 × 0.85
      WEANERS: 4.86, // Restocker Yearling Steer #2
      YEARLINGS: 4.6, // Young Cattle #14
      HEIFER_24_30M: 3.98, // Restocker Yearling Heifer #12
      HEIFER_30_36M: 4.2, // Feeder Heifer #17
      STEER_18_24M: 4.71, // Feeder Steer #3
      STEER_24_36M: 4.42, // Heavy Steer #4
      COW_FIRST_CALF: 4.2, // Processor Cow #13 × 1.10
      COW_MATURE: 3.81, // Processor Cow #13
      BULL_BREEDING: 4.86, // Heavy Steer #4 × 1.10
    },
    sheep: {
      LAMB_UNWEANED: 4.68, // Restocker Lamb #10 × 0.90, cwt→lwt
      LAMB_WEANER: 4.9, // Light Lamb #6, cwt→lwt
      HOGGET: 5.2, // Trade Lamb #7, cwt→lwt
      MAIDEN_EWE: 3.79, // Mutton #11 × 1.10, cwt→lwt
      EWE_FIRST_LAMB: 3.45, // Mutton #11, cwt→lwt
      EWE_MATURE: 3.45, // Mutton #11, cwt→lwt
      WETHER_12_24M: 5.2, // Trade Lamb #7, cwt→lwt
      WETHER_24M_PLUS: 3.45, // Mutton #11, cwt→lwt
      RAM_BREEDING: 5.73, // Heavy Lamb #8 × 1.10, cwt→lwt
    },
  },
}

// Returns the rates for the most recent available quarter.
export function getLatestQuarterlyRates(): QuarterlyValuationRates {
  const keys = Object.keys(QUARTERLY_VALUATION_RATES).sort() as QuarterKey[]
  const latest = keys.at(-1)
  if (!latest) throw new Error('No quarterly valuation rates available')
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return QUARTERLY_VALUATION_RATES[latest]!
}

// Returns rates for the quarter containing the given date.
// - Returns null for dates before our earliest available quarter (no data).
// - Returns the latest available quarter for dates beyond our data range.
export function getRatesForDate(date: Date): QuarterlyValuationRates | null {
  const year = date.getFullYear()
  const quarter = Math.ceil((date.getMonth() + 1) / 3) as 1 | 2 | 3 | 4
  const key: QuarterKey = `${year}-Q${quarter}`

  if (key in QUARTERLY_VALUATION_RATES) {
    return QUARTERLY_VALUATION_RATES[key]!
  }

  const keys = Object.keys(QUARTERLY_VALUATION_RATES).sort() as QuarterKey[]
  const earliest = keys[0]!

  // Date is before our data — return null rather than guess
  if (key < earliest) return null

  // Date is beyond our data — use the most recent available quarter
  return getLatestQuarterlyRates()
}
